import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = () => {
  // Detect if we're on the reverse proxy path
  const isReverseProxy = window.location.pathname.startsWith('/waermepumpen');
  const baseUrl = 'https://www.solartechnik-lauckner.de';
  const canonicalUrl = `${baseUrl}/waermepumpen`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>Wärmepumpen Berlin | Bis 70% Förderung | Solartechnik</title>
      <meta name="title" content="Wärmepumpen Berlin | Bis 70% Förderung | Solartechnik" />
      <meta name="description" content="Nie wieder Öl und Gas! Bis zu 70% Förderung für Ihre Wärmepumpe. Solartechnik Lauckner – Meisterbetrieb seit 13 Jahren in Berlin & Brandenburg." />
      <meta name="author" content="Solartechnik Lauckner" />
      <meta name="keywords" content="Wärmepumpe Berlin, Brandenburg Förderung, Heizung nachhaltig, Photovoltaik, Energiesystem, Oktoberfest Angebot, Komplettlösung" />
      <meta name="geo.region" content="DE-BE" />
      <meta name="geo.placename" content="Berlin, Brandenburg" />
      
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hreflang="de" href={canonicalUrl} />
      <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Wärmepumpen in Berlin & Brandenburg | Bis 70% Förderung – Solartechnik Lauckner" />
      <meta property="og:description" content="Nie wieder Öl und Gas! Sichern Sie sich exklusive Herbst-Konditionen für Ihre Wärmepumpe. Bis zu 70% Förderung möglich." />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="Solartechnik Lauckner" />
      <meta property="og:image" content={`${baseUrl}/static/og/waermepumpen-angebot.jpg`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="de_DE" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Wärmepumpen Berlin & Brandenburg | Bis 70% Förderung" />
      <meta name="twitter:description" content="Oktoberfest-Angebot: Exklusive Herbst-Konditionen für Ihre Wärmepumpe!" />
      <meta name="twitter:image" content={`${baseUrl}/static/og/waermepumpen-angebot.jpg`} />

      {/* Structured Data - WebPage */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${canonicalUrl}#webpage`,
          "url": canonicalUrl,
          "name": "Wärmepumpen Berlin & Brandenburg | Bis 70% Förderung – Solartechnik Lauckner",
          "description": "Nie wieder Öl und Gas! Sichern Sie sich bis zu 70% Förderung für Ihre Wärmepumpe in Berlin & Brandenburg."
        })}
      </script>

      {/* Structured Data - LocalBusiness */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Solartechnik Lauckner",
          "url": `${baseUrl}/`,
          "telephone": "+49 (0) 30 55 65 39 17",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Oranienburger Straße 203",
            "postalCode": "13437",
            "addressLocality": "Berlin",
            "addressCountry": "DE"
          },
          "areaServed": ["Berlin", "Brandenburg"]
        })}
      </script>

      {/* Structured Data - Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Solartechnik Lauckner",
          "alternateName": "SOLARtechnik Lauckner",
          "url": `${baseUrl}/`,
          "logo": `${baseUrl}/static/logo-solartechnik-lauckner.png`,
          "image": `${baseUrl}/static/og/waermepumpen-angebot.jpg`,
          "description": "Ihr Experte für Wärmepumpen, Photovoltaik und nachhaltige Heiztechnik in Berlin & Brandenburg",
          "telephone": "+49 (0) 30 55 65 39 17",
          "email": "info@solartechnik-lauckner.de",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Oranienburger Straße 203",
            "postalCode": "13437",
            "addressLocality": "Berlin",
            "addressRegion": "Berlin",
            "addressCountry": "DE"
          },
          "areaServed": [
            {"@type": "State", "name": "Berlin", "alternateName": "DE-BE"},
            {"@type": "State", "name": "Brandenburg", "alternateName": "DE-BB"}
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+49 (0) 30 55 65 39 17",
            "contactType": "customer service",
            "areaServed": ["DE-BE", "DE-BB"],
            "availableLanguage": "German",
            "contactOption": "TollFree"
          },
          "sameAs": [
            "https://www.google.com/maps/place/Solartechnik+Lauckner"
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Wärmepumpen & Energiesysteme",
            "itemListElement": [
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Wärmepumpen Installation"}},
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Photovoltaik Anlagen"}},
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Stromspeicher Systeme"}},
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Smart-Home Integration"}}
            ]
          }
        })}
      </script>

      {/* Structured Data - Offer */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Offer",
          "name": "Oktoberfest Wärmepumpen-Angebot 2025",
          "description": "Exklusive Herbst-Konditionen für Wärmepumpen und Komplettlösungen. Sichern Sie sich bis zu 70% Förderung!",
          "validFrom": "2025-09-01",
          "validThrough": "2025-10-31",
          "url": canonicalUrl,
          "category": "Wärmepumpe Installation",
          "areaServed": [
            {"@type": "State", "name": "Berlin"},
            {"@type": "State", "name": "Brandenburg"}
          ],
          "seller": {
            "@type": "Organization",
            "name": "Solartechnik Lauckner",
            "telephone": "+49 (0) 30 55 65 39 17",
            "url": `${baseUrl}/`
          },
          "itemOffered": {
            "@type": "Service",
            "name": "Wärmepumpen Installation mit Förderberatung",
            "description": "Komplette Wärmepumpen-Installation inklusive Förderberatung und Komplettlösung",
            "provider": "Solartechnik Lauckner"
          },
          "priceSpecification": {
            "@type": "PriceSpecification",
            "description": "Individuelle Preisgestaltung mit bis zu 70% staatlicher Förderung"
          }
        })}
      </script>

      {/* Structured Data - Service */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Wärmepumpen Installation Berlin Brandenburg",
          "description": "Professionelle Installation von Luft-Wasser und Sole-Wasser Wärmepumpen mit staatlicher Förderung bis 70%",
          "provider": {
            "@type": "Organization",
            "name": "Solartechnik Lauckner"
          },
          "areaServed": [
            {"@type": "City", "name": "Berlin"},
            {"@type": "State", "name": "Brandenburg"}
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Wärmepumpen Services",
            "itemListElement": [
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Luft-Wasser Wärmepumpe Installation"}},
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Sole-Wasser Wärmepumpe Installation"}},
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Förderberatung"}},
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Wartung & Service"}}
            ]
          }
        })}
      </script>

      {/* Structured Data - BreadcrumbList */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Start", "item": `${baseUrl}/`},
            {"@type": "ListItem", "position": 2, "name": "Wärmepumpen", "item": canonicalUrl}
          ]
        })}
      </script>
    </Helmet>
  );
};

export default SEO;
